import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import Hero from '../components/Hero';
import { FileText, CreditCard, ChevronLeft, ChevronRight, Clock } from 'lucide-react';
import dragonBg from '../assets/dragon.png';
import MenuViewer from '../components/MenuViewer';

const CarouselCard = ({ image, title, description }: { image: string; title: string; description: string }) => (
  <div className="relative flex-shrink-0 w-[350px] md:w-[400px]">
    <div className="relative overflow-hidden rounded-2xl shadow-lg">
      <img 
        src={image} 
        alt="" 
        className="w-full h-[300px] md:h-[350px] object-cover"
      />
    </div>
  </div>
);

const ImageCarousel = React.memo(({ 
  images, 
  currentIndex, 
  setCurrentIndex 
}: { 
  images: string[], 
  currentIndex: number, 
  setCurrentIndex: (index: number) => void 
}) => {
  const currentIndexRef = useRef(currentIndex);
  
  // Keep ref in sync with prop
  useEffect(() => {
    currentIndexRef.current = currentIndex;
  }, [currentIndex]);

  const nextSlide = () => {
    setCurrentIndex((currentIndex + 1) % images.length);
  };

  const prevSlide = () => {
    setCurrentIndex(currentIndex === 0 ? images.length - 1 : currentIndex - 1);
  };

  // Auto-rotate images every 5 seconds - TEMPORARILY DISABLED
  // useEffect(() => {
  //   const interval = setInterval(() => {
  //     const nextIndex = (currentIndexRef.current + 1) % images.length;
  //     setCurrentIndex(nextIndex);
  //   }, 5000);

  //   return () => clearInterval(interval);
  // }, [images.length, setCurrentIndex]);

  return (
    <div className="relative w-full h-96 group">
      <div className="relative w-full h-full overflow-hidden rounded-lg">
        <div 
          className="flex transition-transform duration-500 ease-in-out h-full"
          style={{ transform: `translateX(-${currentIndex * 100}%)` }}
        >
          {images.map((image, index) => (
            <div
              key={index}
              className="w-full h-full flex-shrink-0"
            >
              <img
                src={image}
                alt={`Restaurant view ${index + 1}`}
                className="w-full h-full object-cover"
              />
            </div>
          ))}
        </div>
      </div>
      
      <button
        onClick={prevSlide}
        className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/50 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"
      >
        <ChevronLeft size={24} />
      </button>
      
      <button
        onClick={nextSlide}
        className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"
      >
        <ChevronRight size={24} />
      </button>

      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
        {images.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentIndex(index)}
            className={`w-2 h-2 rounded-full transition-all duration-300 ${
              index === currentIndex ? 'bg-white w-8' : 'bg-white/50'
            }`}
          />
        ))}
      </div>
    </div>
  );
});

const Home: React.FC = () => {
  const { t } = useTranslation();
  const [carousel1Index, setCarousel1Index] = useState(0);
  
  // Menu images
  const menuImages: string[] = [
    '/assets/menu/Menu-1-01.png',
    '/assets/menu/Menu-1-02.png',
    '/assets/menu/Menu-1-03.png',
    '/assets/menu/Menu-1-04.png',
    '/assets/menu/Menu-1-05.png',
    '/assets/menu/Menu-1-06.png',
    '/assets/menu/Menu-1-07.png',
    '/assets/menu/Menu-1-08.png',
    '/assets/menu/Menu-1-09.png',
    '/assets/menu/Menu-1-10.png',
    '/assets/menu/Menu-1-11.png',
    '/assets/menu/Menu-1-12.png',
    '/assets/menu/Menu-1-13.png',
  ];

  // Ensure page always loads at the top with multiple safeguards
  useEffect(() => {
    // Immediate scroll to top
    window.scrollTo(0, 0);
    
    // Prevent any scroll events during initial load
    const preventScroll = (e: Event) => {
      e.preventDefault();
      window.scrollTo(0, 0);
    };
    
    // Add scroll prevention
    window.addEventListener('scroll', preventScroll, { passive: false });
    
    // Additional safeguard with timeout
    const scrollTimeout = setTimeout(() => {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }, 100);
    
    // Another safeguard after DOM is fully loaded
    const scrollTimeout2 = setTimeout(() => {
      window.scrollTo(0, 0);
    }, 500);
    
    // Remove scroll prevention after page is stable
    const removePreventionTimeout = setTimeout(() => {
      window.removeEventListener('scroll', preventScroll);
    }, 1000);
    
    return () => {
      clearTimeout(scrollTimeout);
      clearTimeout(scrollTimeout2);
      clearTimeout(removePreventionTimeout);
      window.removeEventListener('scroll', preventScroll);
    };
  }, []);

  // Memoize the setter to prevent unnecessary re-renders
  const setCarousel1 = useCallback((index: number) => setCarousel1Index(index), []);
  
  const DragonBackground = ({ children, className }: { children: React.ReactNode; className?: string }) => (
    <section className={`${className} relative`}>
      <div 
        className="absolute inset-0 opacity-5 pointer-events-none"
        style={{
          backgroundImage: `url(${dragonBg})`,
          backgroundSize: '200px 200px',
          backgroundRepeat: 'repeat',
        }}
      />
      <div className="relative">
        {children}
      </div>
    </section>
  );

  const schedule = [
    { day: t('monday'), lunch: t('lunchHours'), dinner: t('dinnerHours') },
    { day: t('tuesday'), lunch: t('lunchHours'), dinner: t('dinnerHours') },
    { day: t('wednesday'), lunch: t('lunchHours'), dinner: t('dinnerHours') },
    { day: t('thursday'), lunch: t('lunchHours'), dinner: t('dinnerHours') },
    { day: t('friday'), lunch: t('lunchHours'), dinner: t('dinnerHours') },
    { day: t('saturday'), lunch: t('lunchHours'), dinner: t('dinnerHours') },
    { day: t('sunday'), lunch: t('lunchHours'), dinner: t('dinnerHours') }
  ];

  // Food images for carousel 1
  const carousel1Images = [
    '/assets/IMG_9089.jpg',
    '/assets/IMG_9090.jpg',
    '/assets/IMG_9091.jpg',
    '/assets/IMG_9092.jpg',
    '/assets/IMG_9094.JPG',
  ];

  return (
    <>
      {/* 1. Hero Section */}
      <Hero />

      {/* Elegant Dynamic Carousel Section */}
      <section className="py-20 md:py-32 bg-gradient-to-b from-white to-gray-50">
        <div className="container mx-auto px-4">
          {/* Section Title */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-display text-gray-900 mb-4">
              {t('carouselTitle1')}
            </h2>
            <div className="w-24 h-1 bg-carnival-burgundy mx-auto mb-4" />
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              {t('carouselDescription')}
            </p>
          </motion.div>

          {/* Carousel Container */}
          <div className="relative">
            {/* Carousel Track */}
            <div className="overflow-hidden">
              <motion.div
                className="flex gap-6"
                animate={{ x: [0, -8640] }}
                transition={{
                  x: {
                    repeat: Infinity,
                    duration: 80,
                    ease: "linear",
                    repeatType: "loop",
                  }
                }}
              >
                {/* First set of images */}
                <div className="flex gap-6 flex-shrink-0">
                  <CarouselCard 
                    image="/assets/carnival/WechatIMG21.jpg" 
                    title={t('vipSpaceTitle')} 
                    description={t('privateRoomsDescription')}
                  />
                  <CarouselCard 
                    image="/assets/wetransfer_image00001-jpeg_2025-06-30_0331/decor2.jpeg" 
                    title="" 
                    description=""
                  />
                  <CarouselCard 
                    image="/assets/carnival/WechatIMG25.jpg" 
                    title={t('seafoodTitle')} 
                    description={t('freshGuaranteedDescription')}
                  />
                  <CarouselCard 
                    image="/assets/carnival/WechatIMG27.jpg" 
                    title={t('chocolateFountainTitle')} 
                    description={t('gourmetParadiseDescription')}
                  />
                  <CarouselCard 
                    image="/assets/carnival/WechatIMG28.jpg" 
                    title={t('saladBarTitle')} 
                    description={t('freshnessVarietyDescription')}
                  />
                  <CarouselCard 
                    image="/assets/carnival/WechatIMG29.jpg" 
                    title={t('wokStationTitle')} 
                    description={t('liveKitchenDescription')}
                  />
                  <CarouselCard 
                    image="/assets/carnival/WechatIMG30.jpg" 
                    title={t('completeBuffetTitle')} 
                    description={t('dishesCountDescription')}
                  />
                  <CarouselCard 
                    image="/assets/carnival/WechatIMG31.jpg" 
                    title={t('homemadeDessertsTitle')} 
                    description={t('freshPastriesDescription')}
                  />
                  <CarouselCard 
                    image="/assets/wetransfer_image00001-jpeg_2025-06-30_0331/decor1.jpeg" 
                    title="" 
                    description=""
                  />
                  <CarouselCard 
                    image="/assets/wetransfer_image00001-jpeg_2025-06-30_0331/decor3.jpeg" 
                    title="" 
                    description=""
                  />
                  <CarouselCard 
                    image="/assets/wetransfer_image00001-jpeg_2025-06-30_0331/decor4.jpeg" 
                    title="" 
                    description=""
                  />
                  <CarouselCard 
                    image="/assets/wetransfer_image00001-jpeg_2025-06-30_0331/decor5.jpeg" 
                    title="" 
                    description=""
                  />
                  <CarouselCard 
                    image="/assets/carnival/WechatIMG11.jpg" 
                    title="" 
                    description=""
                  />
                  <CarouselCard 
                    image="/assets/carnival/WechatIMG12.jpg" 
                    title="" 
                    description=""
                  />
                  <CarouselCard 
                    image="/assets/carnival/WechatIMG13.jpg" 
                    title="" 
                    description=""
                  />
                  <CarouselCard 
                    image="/assets/carnival/WechatIMG14.jpg" 
                    title="" 
                    description=""
                  />
                  <CarouselCard 
                    image="/assets/carnival/WechatIMG15.jpg" 
                    title="" 
                    description=""
                  />
                  <CarouselCard 
                    image="/assets/carnival/WechatIMG16.jpg" 
                    title="" 
                    description=""
                  />
                  <CarouselCard 
                    image="/assets/carnival/WechatIMG17.jpg" 
                    title="" 
                    description=""
                  />
                  <CarouselCard 
                    image="/assets/carnival/WechatIMG18.jpg" 
                    title="" 
                    description=""
                  />
                </div>
                
                {/* Duplicate for seamless loop */}
                <div className="flex gap-6 flex-shrink-0">
                  <CarouselCard 
                    image="/assets/carnival/WechatIMG21.jpg" 
                    title={t('vipSpaceTitle')} 
                    description={t('privateRoomsDescription')}
                  />
                  <CarouselCard 
                    image="/assets/wetransfer_image00001-jpeg_2025-06-30_0331/decor2.jpeg" 
                    title="" 
                    description=""
                  />
                  <CarouselCard 
                    image="/assets/carnival/WechatIMG25.jpg" 
                    title={t('seafoodTitle')} 
                    description={t('freshGuaranteedDescription')}
                  />
                  <CarouselCard 
                    image="/assets/carnival/WechatIMG27.jpg" 
                    title={t('chocolateFountainTitle')} 
                    description={t('gourmetParadiseDescription')}
                  />
                  <CarouselCard 
                    image="/assets/carnival/WechatIMG28.jpg" 
                    title={t('saladBarTitle')} 
                    description={t('freshnessVarietyDescription')}
                  />
                  <CarouselCard 
                    image="/assets/carnival/WechatIMG29.jpg" 
                    title={t('wokStationTitle')} 
                    description={t('liveKitchenDescription')}
                  />
                  <CarouselCard 
                    image="/assets/carnival/WechatIMG30.jpg" 
                    title={t('completeBuffetTitle')} 
                    description={t('dishesCountDescription')}
                  />
                  <CarouselCard 
                    image="/assets/carnival/WechatIMG31.jpg" 
                    title={t('homemadeDessertsTitle')} 
                    description={t('freshPastriesDescription')}
                  />
                  <CarouselCard 
                    image="/assets/wetransfer_image00001-jpeg_2025-06-30_0331/decor1.jpeg" 
                    title="" 
                    description=""
                  />
                  <CarouselCard 
                    image="/assets/wetransfer_image00001-jpeg_2025-06-30_0331/decor3.jpeg" 
                    title="" 
                    description=""
                  />
                  <CarouselCard 
                    image="/assets/wetransfer_image00001-jpeg_2025-06-30_0331/decor4.jpeg" 
                    title="" 
                    description=""
                  />
                  <CarouselCard 
                    image="/assets/wetransfer_image00001-jpeg_2025-06-30_0331/decor5.jpeg" 
                    title="" 
                    description=""
                  />
                  <CarouselCard 
                    image="/assets/carnival/WechatIMG11.jpg" 
                    title="" 
                    description=""
                  />
                  <CarouselCard 
                    image="/assets/carnival/WechatIMG12.jpg" 
                    title="" 
                    description=""
                  />
                  <CarouselCard 
                    image="/assets/carnival/WechatIMG13.jpg" 
                    title="" 
                    description=""
                  />
                  <CarouselCard 
                    image="/assets/carnival/WechatIMG14.jpg" 
                    title="" 
                    description=""
                  />
                  <CarouselCard 
                    image="/assets/carnival/WechatIMG15.jpg" 
                    title="" 
                    description=""
                  />
                  <CarouselCard 
                    image="/assets/carnival/WechatIMG16.jpg" 
                    title="" 
                    description=""
                  />
                  <CarouselCard 
                    image="/assets/carnival/WechatIMG17.jpg" 
                    title="" 
                    description=""
                  />
                  <CarouselCard 
                    image="/assets/carnival/WechatIMG18.jpg" 
                    title="" 
                    description=""
                  />
                </div>
                
                {/* Third copy for smoother loop */}
                <div className="flex gap-6 flex-shrink-0">
                  <CarouselCard 
                    image="/assets/carnival/WechatIMG21.jpg" 
                    title={t('vipSpaceTitle')} 
                    description={t('privateRoomsDescription')}
                  />
                  <CarouselCard 
                    image="/assets/wetransfer_image00001-jpeg_2025-06-30_0331/decor2.jpeg" 
                    title="" 
                    description=""
                  />
                  <CarouselCard 
                    image="/assets/carnival/WechatIMG25.jpg" 
                    title={t('seafoodTitle')} 
                    description={t('freshGuaranteedDescription')}
                  />
                  <CarouselCard 
                    image="/assets/carnival/WechatIMG27.jpg" 
                    title={t('chocolateFountainTitle')} 
                    description={t('gourmetParadiseDescription')}
                  />
                  <CarouselCard 
                    image="/assets/carnival/WechatIMG28.jpg" 
                    title={t('saladBarTitle')} 
                    description={t('freshnessVarietyDescription')}
                  />
                  <CarouselCard 
                    image="/assets/carnival/WechatIMG29.jpg" 
                    title={t('wokStationTitle')} 
                    description={t('liveKitchenDescription')}
                  />
                  <CarouselCard 
                    image="/assets/carnival/WechatIMG30.jpg" 
                    title={t('completeBuffetTitle')} 
                    description={t('dishesCountDescription')}
                  />
                  <CarouselCard 
                    image="/assets/carnival/WechatIMG31.jpg" 
                    title={t('homemadeDessertsTitle')} 
                    description={t('freshPastriesDescription')}
                  />
                  <CarouselCard 
                    image="/assets/wetransfer_image00001-jpeg_2025-06-30_0331/decor1.jpeg" 
                    title="" 
                    description=""
                  />
                  <CarouselCard 
                    image="/assets/wetransfer_image00001-jpeg_2025-06-30_0331/decor3.jpeg" 
                    title="" 
                    description=""
                  />
                  <CarouselCard 
                    image="/assets/wetransfer_image00001-jpeg_2025-06-30_0331/decor4.jpeg" 
                    title="" 
                    description=""
                  />
                  <CarouselCard 
                    image="/assets/wetransfer_image00001-jpeg_2025-06-30_0331/decor5.jpeg" 
                    title="" 
                    description=""
                  />
                  <CarouselCard 
                    image="/assets/carnival/WechatIMG11.jpg" 
                    title="" 
                    description=""
                  />
                  <CarouselCard 
                    image="/assets/carnival/WechatIMG12.jpg" 
                    title="" 
                    description=""
                  />
                  <CarouselCard 
                    image="/assets/carnival/WechatIMG13.jpg" 
                    title="" 
                    description=""
                  />
                  <CarouselCard 
                    image="/assets/carnival/WechatIMG14.jpg" 
                    title="" 
                    description=""
                  />
                  <CarouselCard 
                    image="/assets/carnival/WechatIMG15.jpg" 
                    title="" 
                    description=""
                  />
                  <CarouselCard 
                    image="/assets/carnival/WechatIMG16.jpg" 
                    title="" 
                    description=""
                  />
                  <CarouselCard 
                    image="/assets/carnival/WechatIMG17.jpg" 
                    title="" 
                    description=""
                  />
                  <CarouselCard 
                    image="/assets/carnival/WechatIMG18.jpg" 
                    title="" 
                    description=""
                  />
                </div>
              </motion.div>
            </div>
          </div>

          {/* CTA Button */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-center mt-16"
          >
            <a 
              href="#menu" 
              className="inline-block px-8 py-4 bg-carnival-burgundy text-white font-medium rounded-full shadow-lg"
            >
              {t('discoverOurMenuBtn')}
            </a>
          </motion.div>
        </div>
      </section>

      {/* 3. Menu PDF */}
      <section id="menu" className="py-20 bg-site-bg">
        <div className="section-padding">
          <div className="max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <FileText size={48} className="text-carnival-burgundy mx-auto mb-4" />
              <h2 className="text-4xl md:text-5xl font-display mb-4 text-carnival-charcoal">
                {t('menuSectionTitle')}
              </h2>
              <div className="w-24 h-px bg-carnival-burgundy mx-auto" />
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
            >
              <MenuViewer
                menuImages={menuImages}
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* 4. Pricing Table - UPDATED WITH NEW COMPLEX PRICING */}
      <section className="py-20 bg-white relative overflow-hidden">
        <div 
          className="absolute left-0 top-0 bottom-0 w-1/3 opacity-5 pointer-events-none"
          style={{
            backgroundImage: `url(${dragonBg})`,
            backgroundSize: '200px 200px',
            backgroundRepeat: 'repeat',
          }}
        />
        <div className="section-padding relative z-10">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-8 md:mb-12"
          >
            <h2 className="text-4xl md:text-6xl font-display mb-4 text-gray-800">
              {t('pricingSectionTitle')}
            </h2>
            <p className="text-xl md:text-2xl text-gray-600 mb-6">
              {t('pricingSubtitle')}
            </p>
            <div className="w-32 h-1 bg-gradient-to-r from-transparent via-black to-transparent mx-auto" />
          </motion.div>

          <div className="max-w-6xl mx-auto">
            {/* Lunch Prices */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mb-6 md:mb-12"
            >
              <h3 className="text-3xl md:text-4xl font-display text-gray-800 mb-8 text-center flex items-center justify-center gap-3">
                <span className="text-black">☀️</span>
                {t('lunchPrices')}
              </h3>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
                {/* Weekday Lunch */}
                <div className="bg-white px-6 md:px-8 py-8 md:py-10 rounded-xl shadow-xl border border-gray-100 hover:shadow-2xl transition-shadow duration-300">
                  <h4 className="text-xl md:text-2xl font-display text-gray-800 mb-4 md:mb-6 font-semibold">
                    {t('mondayToFriday')}
                  </h4>
                  <div className="space-y-2 md:space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="font-body text-gray-700 text-lg">{t('adult')}</span>
                      <span className="text-2xl md:text-3xl font-display text-carnival-burgundy font-bold">16.90€</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="font-body text-gray-700 text-lg">{t('childUnder12')}</span>
                      <span className="text-2xl md:text-3xl font-display text-carnival-burgundy font-bold">12.00€</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="font-body text-gray-700 text-lg">{t('childUnder5')}</span>
                      <span className="text-2xl md:text-3xl font-display text-carnival-burgundy font-bold">5.00€</span>
                    </div>
                  </div>
                </div>

                {/* Weekend/Holiday Lunch */}
                <div className="bg-white px-6 md:px-8 py-8 md:py-10 rounded-xl shadow-xl border border-gray-100 hover:shadow-2xl transition-shadow duration-300">
                  <h4 className="text-xl md:text-2xl font-display text-gray-800 mb-4 md:mb-6 font-semibold">
                    {t('weekendHolidayLunch')}
                  </h4>
                  <div className="space-y-2 md:space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="font-body text-gray-700 text-lg">{t('adult')}</span>
                      <span className="text-2xl md:text-3xl font-display text-carnival-burgundy font-bold">24.90€</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="font-body text-gray-700 text-lg">{t('childUnder12')}</span>
                      <span className="text-2xl md:text-3xl font-display text-carnival-burgundy font-bold">12.00€</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="font-body text-gray-700 text-lg">{t('childUnder5')}</span>
                      <span className="text-2xl md:text-3xl font-display text-carnival-burgundy font-bold">5.00€</span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Dinner Prices */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <h3 className="text-3xl md:text-4xl font-display text-gray-800 mt-8 md:mt-12 mb-8 text-center flex items-center justify-center gap-3">
                <span className="text-black">🌙</span>
                {t('dinnerPrices')}
              </h3>
              
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
                {/* Monday-Thursday Dinner */}
                <div className="bg-white px-6 md:px-8 py-8 md:py-10 rounded-xl shadow-xl border border-gray-100 hover:shadow-2xl transition-shadow duration-300">
                  <h4 className="text-xl md:text-2xl font-display text-gray-800 mb-4 md:mb-6 font-semibold">
                    {t('mondayToThursday')}
                  </h4>
                  <div className="space-y-2 md:space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="font-body text-gray-700 text-lg">{t('adult')}</span>
                      <span className="text-2xl md:text-3xl font-display text-carnival-burgundy font-bold">22.90€</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="font-body text-gray-700 text-lg">{t('childUnder12')}</span>
                      <span className="text-2xl md:text-3xl font-display text-carnival-burgundy font-bold">12.00€</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="font-body text-gray-700 text-lg">{t('childUnder5')}</span>
                      <span className="text-2xl md:text-3xl font-display text-carnival-burgundy font-bold">5.00€</span>
                    </div>
                  </div>
                </div>

                {/* Friday/Saturday Dinner */}
                <div className="bg-white px-6 md:px-8 py-8 md:py-10 rounded-xl shadow-xl border border-gray-100 hover:shadow-2xl transition-shadow duration-300">
                  <h4 className="text-xl md:text-2xl font-display text-gray-800 mb-4 md:mb-6 font-semibold">
                    {t('fridaySaturday')}
                  </h4>
                  <div className="space-y-2 md:space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="font-body text-gray-700 text-lg">{t('adult')}</span>
                      <span className="text-2xl md:text-3xl font-display text-carnival-burgundy font-bold">25.90€</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="font-body text-gray-700 text-lg">{t('childUnder12')}</span>
                      <span className="text-2xl md:text-3xl font-display text-carnival-burgundy font-bold">12.00€</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="font-body text-gray-700 text-lg">{t('childUnder5')}</span>
                      <span className="text-2xl md:text-3xl font-display text-carnival-burgundy font-bold">5.00€</span>
                    </div>
                  </div>
                </div>

                {/* Sunday/Holiday Dinner */}
                <div className="bg-white px-6 md:px-8 py-8 md:py-10 rounded-xl shadow-xl border border-gray-100 hover:shadow-2xl transition-shadow duration-300">
                  <h4 className="text-xl md:text-2xl font-display text-gray-800 mb-4 md:mb-6 font-semibold">
                    {t('sundayHoliday')}
                  </h4>
                  <div className="space-y-2 md:space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="font-body text-gray-700 text-lg">{t('adult')}</span>
                      <span className="text-2xl md:text-3xl font-display text-carnival-burgundy font-bold">24.90€</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="font-body text-gray-700 text-lg">{t('childUnder12')}</span>
                      <span className="text-2xl md:text-3xl font-display text-carnival-burgundy font-bold">12.00€</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="font-body text-gray-700 text-lg">{t('childUnder5')}</span>
                      <span className="text-2xl md:text-3xl font-display text-carnival-burgundy font-bold">5.00€</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-6 text-center">
                <p className="text-black text-2xl font-body font-bold border-2 border-black inline-block px-6 py-3 rounded-lg">
                  {t('holidayEve')}
                </p>
                <p className="text-carnival-charcoal/60 text-lg font-body mt-4">
                  {t('childDisclaimer')}
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Découvrez Notre Univers Section */}
      <DragonBackground className="py-20 bg-site-bg">
        <div className="section-padding">

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="relative max-w-4xl mx-auto"
          >
            <h3 className="text-2xl font-display mb-6 text-black text-center tracking-wide">
              {t('ourAmbianceTitle')}
            </h3>
            <ImageCarousel 
              key="carousel1"
              images={carousel1Images} 
              currentIndex={carousel1Index} 
              setCurrentIndex={setCarousel1} 
            />
          </motion.div>
        </div>
      </DragonBackground>

      {/* Opening Hours - Moved here after Notre Ambiance */}
      <section className="py-20 bg-white">
        <div className="section-padding">
          <div className="max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-xl shadow-2xl overflow-hidden border border-gray-100"
            >
              <h3 className="text-3xl font-display text-gray-800 text-center flex items-center justify-center gap-3 p-8 pb-6 bg-gradient-to-r from-black/10 to-carnival-burgundy/10">
                <Clock size={32} className="text-black" />
                {t('hoursTitle')}
              </h3>
              <div>
                <div className="grid grid-cols-3 bg-gradient-to-r from-carnival-burgundy to-carnival-burgundy/90 text-white font-display text-lg">
                  <div className="p-5 text-center font-semibold">{t('dayHeader')}</div>
                  <div className="p-5 text-center border-l border-white/20 font-semibold">{t('lunchHeader')}</div>
                  <div className="p-5 text-center border-l border-white/20 font-semibold">{t('dinnerHeader')}</div>
                </div>
                {schedule.map((item, index) => (
                  <div
                    key={index}
                    className={`grid grid-cols-3 ${index % 2 === 0 ? 'bg-white' : 'bg-carnival-sand/30'}`}
                  >
                    <div className="p-5 font-semibold text-gray-800 text-lg">{item.day}</div>
                    <div className="p-5 text-center text-gray-700 font-medium text-lg">{item.lunch}</div>
                    <div className="p-5 text-center text-gray-700 font-medium text-lg">{item.dinner}</div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 5. Services + Events & Reception */}
      <section className="py-20 bg-site-bg">
        <div className="section-padding">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl md:text-5xl font-display mb-4 text-carnival-charcoal">
              {t('servicesSectionTitle')}
            </h2>
            <div className="w-24 h-px bg-black mx-auto" />
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {/* Services */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white p-10 rounded-2xl shadow-2xl border border-gray-100 hover:shadow-3xl transition-shadow duration-300"
            >
              <h3 className="text-2xl md:text-3xl font-display text-gray-800 mb-8 text-center">
                {t('servicesAvailable')}
              </h3>
              <div className="space-y-4">
                <div className="flex items-start gap-5 p-4 rounded-lg hover:bg-gray-50 transition-colors duration-200">
                  <span className="text-3xl">🍽️</span>
                  <div>
                    <h4 className="font-bold text-gray-800 mb-2 text-lg">{t('selfServiceBuffet')}</h4>
                    <p className="text-gray-600">{t('unlimitedBuffet')}</p>
                  </div>
                </div>
                <div className="flex items-start gap-5 p-4 rounded-lg hover:bg-gray-50 transition-colors duration-200">
                  <span className="text-3xl">❄️</span>
                  <div>
                    <h4 className="font-bold text-gray-800 mb-2 text-lg">{t('airConditioning')}</h4>
                    <p className="text-gray-600">{t('comfortableEnvironment')}</p>
                  </div>
                </div>
                <div className="flex items-start gap-5 p-4 rounded-lg hover:bg-gray-50 transition-colors duration-200">
                  <span className="text-3xl">🚗</span>
                  <div>
                    <h4 className="font-bold text-gray-800 mb-2 text-lg">{t('freeParking')}</h4>
                    <p className="text-gray-600">{t('outdoorParking')}</p>
                  </div>
                </div>
                <div className="flex items-start gap-5 p-4 rounded-lg hover:bg-gray-50 transition-colors duration-200">
                  <span className="text-3xl">🚬</span>
                  <div>
                    <h4 className="font-bold text-gray-800 mb-2 text-lg">{t('smokingArea')}</h4>
                    <p className="text-gray-600">{t('dedicatedSmoking')}</p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Events & Reception */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white p-10 rounded-2xl shadow-2xl border border-gray-100 hover:shadow-3xl transition-shadow duration-300"
            >
              <h3 className="text-2xl md:text-3xl font-display text-gray-800 mb-8 text-center">
                <span className="text-5xl mb-4 block">🎉</span>
                {t('eventsReceptions')}
              </h3>
              <div className="space-y-6">
                <p className="text-carnival-charcoal text-lg text-center font-body">
                  {t('eventsDescription')}
                </p>
                <div className="bg-carnival-cream/50 p-6 rounded-lg">
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <span className="text-black text-xl">💒</span>
                      <span className="text-carnival-charcoal">{t('weddings')}</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-black text-xl">🎂</span>
                      <span className="text-carnival-charcoal">{t('privateEvents')}</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-black text-xl">👥</span>
                      <span className="text-carnival-charcoal">{t('groupReceptions')}</span>
                    </li>
                  </ul>
                </div>
                <p className="text-carnival-charcoal/70 text-sm text-center italic">
                  {t('contactEvents')}
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 6. Payment Methods */}
      <section className="py-20 bg-site-bg">
        <div className="section-padding">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <CreditCard size={48} className="text-black mx-auto mb-4" />
            <h2 className="text-4xl md:text-5xl font-display mb-4 text-carnival-charcoal">
              {t('paymentMethodsTitle')}
            </h2>
            <div className="w-24 h-px bg-black mx-auto" />
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto"
          >
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {/* CB */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300"
              >
                <div className="flex flex-col items-center">
                  <div className="w-16 h-16 bg-carnival-burgundy/10 rounded-full flex items-center justify-center mb-4">
                    <CreditCard size={32} className="text-carnival-burgundy" />
                  </div>
                  <p className="text-carnival-charcoal font-body text-lg font-medium text-center">{t('creditCards')}</p>
                </div>
              </motion.div>
              
              {/* Espèces */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300"
              >
                <div className="flex flex-col items-center">
                  <div className="w-16 h-16 bg-carnival-burgundy/10 rounded-full flex items-center justify-center mb-4">
                    <span className="text-3xl">💵</span>
                  </div>
                  <p className="text-carnival-charcoal font-body text-lg font-medium text-center">{t('cash')}</p>
                </div>
              </motion.div>
              
              {/* Ticket Restaurant */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
                className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300"
              >
                <div className="flex flex-col items-center">
                  <div className="w-16 h-16 bg-carnival-burgundy/10 rounded-full flex items-center justify-center mb-4">
                    <span className="text-3xl">🎫</span>
                  </div>
                  <p className="text-carnival-charcoal font-body text-lg font-medium text-center">{t('mealVouchers')}</p>
                </div>
              </motion.div>
              
              {/* ANCV */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.4 }}
                className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300"
              >
                <div className="flex flex-col items-center">
                  <div className="w-16 h-16 bg-carnival-burgundy/10 rounded-full flex items-center justify-center mb-4">
                    <span className="text-3xl">🏖️</span>
                  </div>
                  <p className="text-carnival-charcoal font-body text-lg font-medium text-center">{t('ancv')}</p>
                </div>
              </motion.div>
            </div>
            
            <p className="text-carnival-charcoal/60 text-sm text-center mt-8 font-semibold">
              {t('checksNotAccepted')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* 7. Notre Philosophie Culinaire */}
      <section className="py-20 bg-site-bg">
        <div className="section-padding">
          <div className="max-w-4xl mx-auto">
            <motion.h2 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-4xl md:text-5xl font-display mb-4 text-carnival-charcoal text-center"
            >
              {t('philosophyTitle')}
            </motion.h2>
            <motion.p 
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="text-2xl font-display text-black text-center mb-4 italic"
            >
              {t('philosophyMotto')}
            </motion.p>
            <div className="space-y-6 text-lg text-carnival-charcoal leading-relaxed font-body">
              <p>{t('philosophyDescription1')}</p>
              <p>{t('philosophyDescription2')}</p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-12">
                <motion.div 
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  className="flex items-center gap-4 bg-white p-6 rounded-lg shadow-lg"
                >
                  <span className="text-black text-2xl">✅</span>
                  <span className="font-body">{t('freshProducts')}</span>
                </motion.div>
                <motion.div 
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  className="flex items-center gap-4 bg-white p-6 rounded-lg shadow-lg"
                >
                  <span className="text-black text-2xl">✅</span>
                  <span className="font-body">{t('homemadeCuisine')}</span>
                </motion.div>
                <motion.div 
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.1 }}
                  className="flex items-center gap-4 bg-white p-6 rounded-lg shadow-lg"
                >
                  <span className="text-black text-2xl">✅</span>
                  <span className="font-body">{t('fastService')}</span>
                </motion.div>
                <motion.div 
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.1 }}
                  className="flex items-center gap-4 bg-white p-6 rounded-lg shadow-lg"
                >
                  <span className="text-black text-2xl">✅</span>
                  <span className="font-body">{t('excellentValue')}</span>
                </motion.div>
              </div>
              
              <p>{t('philosophyDescription3')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* 8. L'équipe & Nos Valeurs */}
      <DragonBackground className="py-20 bg-site-bg">
        <div className="section-padding">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl md:text-5xl font-display mb-4 text-carnival-charcoal">
              <span className="text-4xl mr-3">🤝</span>
              {t('teamValuesTitle')}
            </h2>
            <div className="w-24 h-px bg-black mx-auto" />
          </motion.div>

          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="space-y-6 text-lg text-carnival-charcoal leading-relaxed font-body mb-12"
            >
              <p>{t('teamDescription1')}</p>
              <p>{t('teamDescription2')}</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-gray-100 p-8 rounded-lg"
            >
              <h3 className="text-xl md:text-2xl font-display text-carnival-burgundy mb-6 text-center">
                {t('dailyCommitments')}
              </h3>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <span className="text-2xl">🤗</span>
                  <div>
                    <h4 className="font-semibold text-carnival-charcoal mb-2">{t('warmHospitality')}</h4>
                    <p className="text-carnival-charcoal/70">{t('humanFamilyAtmosphere')}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <span className="text-2xl">🌱</span>
                  <div>
                    <h4 className="font-semibold text-carnival-charcoal mb-2">{t('ecoResponsibility')}</h4>
                    <p className="text-carnival-charcoal/70">{t('activeWasteFight')}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <span className="text-2xl">🎯</span>
                  <div>
                    <h4 className="font-semibold text-carnival-charcoal mb-2">{t('accessibilityForAll')}</h4>
                    <p className="text-carnival-charcoal/70">{t('affordablePricing')}</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </DragonBackground>

      {/* 9. Notre Histoire */}
      <DragonBackground className="py-20 bg-site-bg">
        <div className="section-padding">
          <div className="max-w-4xl mx-auto">
            <motion.h2 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-4xl md:text-5xl font-display mb-4 text-carnival-charcoal text-center"
            >
              {t('historySectionTitle')}
            </motion.h2>
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="w-24 h-px bg-black mx-auto mb-12"
            />
            <motion.div 
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="space-y-6 text-lg text-carnival-charcoal leading-relaxed font-body"
            >
              <p>{t('storyParagraph1')}</p>
              <p>{t('storyParagraph2')}</p>
            </motion.div>
          </div>
        </div>
      </DragonBackground>

      {/* 10. Rules & Information */}
      <section className="py-24 bg-gradient-to-br from-carnival-cream/30 via-white to-gray-50 relative overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-20"
          style={{
            backgroundImage: 'url("/assets/carnival/WechatIMG31.jpg")'
          }}
        />
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-carnival-burgundy/5 rounded-full -translate-y-32 translate-x-32" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-carnival-gold/5 rounded-full translate-y-48 -translate-x-48" />
        
        <div className="section-padding relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-5xl md:text-6xl font-display mb-8 text-carnival-charcoal">
              {t('rulesAndInfo')}
            </h2>
            <div className="flex items-center justify-center gap-4 mb-4">
              <div className="w-20 h-0.5 bg-carnival-burgundy" />
              <div className="w-3 h-3 bg-carnival-burgundy rounded-full" />
              <div className="w-20 h-0.5 bg-carnival-burgundy" />
            </div>
          </motion.div>

          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Waste Warning */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border-l-4 border-red-400 group"
              >
                <p className="font-body text-gray-700 leading-relaxed">{t('wasteWarning')}</p>
              </motion.div>

              {/* No Takeaway */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border-l-4 border-orange-400 group"
              >
                <p className="font-body text-gray-700 leading-relaxed">{t('noTakeaway')}</p>
              </motion.div>

              {/* Wine Contains Sulfites */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
                className="bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border-l-4 border-purple-400 group"
              >
                <p className="font-body text-gray-700 leading-relaxed">{t('wineContainsSulfites')}</p>
              </motion.div>

              {/* Alcohol Warning */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.4 }}
                className="bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border-l-4 border-purple-400 group"
              >
                <p className="font-body text-gray-700 leading-relaxed">{t('alcoholWarning')}</p>
              </motion.div>

              {/* Allergy Info */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.5 }}
                className="bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border-l-4 border-yellow-400 group"
              >
                <p className="font-body text-gray-700 leading-relaxed">{t('allergyInfo')}</p>
              </motion.div>

              {/* Health Advice */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.6 }}
                className="bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border-l-4 border-green-400 group"
              >
                <p className="font-body text-gray-700 leading-relaxed">{t('healthAdvice')}</p>
              </motion.div>
            </div>

            {/* Prices Net */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.7 }}
              className="mt-12 text-center"
            >
              <div className="inline-block bg-white px-8 py-4 rounded-full shadow-lg">
                <p className="font-display text-lg text-carnival-charcoal">
                  {t('pricesNet')}
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

    </>
  );
};

export default Home;